import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Spinner,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { Wallet, ExternalLink, CreditCard, CheckCircle2, Plus, Lock } from 'lucide-react';
import {
  useGenerateVietQR,
  useCreateMoMoPayment,
  useCreatePayPalOrder,
  useMoMoStatus,
  useChargeSavedMethod,
  generatePaymentReference,
  convertVndToUsd,
  type VietQRResult,
  type MoMoResult,
} from '@/api/billing-payments';
import { usePaymentMethods } from '@/api/billing';
import { BraintreeCardForm } from './BraintreeCardForm';
import { CARD_PAYMENTS_ENABLED } from './card-payments-flag';
import { formatCurrency } from '@/lib/format-currency';
import { subscribePaymentResult } from '@/lib/payment-broadcast';
import {
  trackBeginCheckout,
  trackAddPaymentInfo,
  trackAddPaymentMethod,
  trackPurchase,
} from '@/lib/analytics-events';
import { brand } from '@/config/brand';
import styles from './Billing.module.css';

// MoMo payments are not live yet — hide the tab until the integration is on-air.
// Flip to true to re-enable.
const MOMO_ENABLED = false;

interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  amount: number;
  customerId?: string | number;
  /** Invoice creation datetime — anchors the payment reference stamp so it stays
   *  stable per invoice instead of changing each time the dialog opens. */
  createdAt?: string;
}

function dataUri(b64: string): string {
  return b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
}

// Unix-ms stamp for the payment reference, anchored to the invoice creation time
// so the same invoice always yields the same reference. Falls back to now() when
// the datetime is missing or unparseable. Normalizes "YYYY-MM-DD HH:mm:ss" (space
// separator) to ISO so non-Chromium parsers don't return NaN.
function referenceStamp(createdAt?: string): number {
  if (!createdAt) return Date.now();
  const ms = new Date(createdAt.replace(' ', 'T')).getTime();
  return Number.isNaN(ms) ? Date.now() : ms;
}

export function PaymentDialog({ open, onOpenChange, invoiceId, amount, customerId, createdAt }: PaymentDialogProps) {
  const stamp = referenceStamp(createdAt);
  const qc = useQueryClient();
  const { show } = useToast();
  // Checkout funnel entry — fires when the pay dialog opens for an invoice.
  useEffect(() => {
    if (open) trackBeginCheckout(invoiceId, amount);
  }, [open, invoiceId, amount]);

  // PayPal completes in a separate tab; listen for the cross-tab success signal
  // for THIS invoice so the dialog auto-closes + balances refresh here.
  useEffect(() => {
    if (!open) return;
    return subscribePaymentResult((r) => {
      if (r.status === 'success' && String(r.invoiceId) === String(invoiceId)) {
        qc.invalidateQueries({ queryKey: ['billing'] });
        show({ tone: 'success', title: 'Payment received', description: `Invoice #${invoiceId} is paid.` });
        onOpenChange(false);
      }
    });
  }, [open, invoiceId, onOpenChange, qc, show]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Pay invoice #{invoiceId}</DialogTitle>
          <DialogDescription>{formatCurrency(amount)} due. Choose a payment method.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Tabs defaultValue="bank">
            <TabsList>
              <TabsTrigger value="bank">Bank</TabsTrigger>
              {/* MoMo hidden until its integration is live. */}
              {MOMO_ENABLED && <TabsTrigger value="momo">MoMo</TabsTrigger>}
              <TabsTrigger value="paypal">PayPal</TabsTrigger>
              {/* Card tab hidden until Braintree is on-air (see card-payments-flag). */}
              {CARD_PAYMENTS_ENABLED && <TabsTrigger value="card">Card</TabsTrigger>}
            </TabsList>

            <TabsContent value="bank">
              <BankTab invoiceId={invoiceId} amount={amount} customerId={customerId} stamp={stamp} />
            </TabsContent>
            {MOMO_ENABLED && (
              <TabsContent value="momo">
                <MoMoTab invoiceId={invoiceId} amount={amount} />
              </TabsContent>
            )}
            <TabsContent value="paypal">
              <PayPalTab invoiceId={invoiceId} amount={amount} customerId={customerId} stamp={stamp} />
            </TabsContent>
            {CARD_PAYMENTS_ENABLED && (
              <TabsContent value="card">
                <CardTab invoiceId={invoiceId} amount={amount} />
              </TabsContent>
            )}
          </Tabs>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

// Static company bank account for manual transfers — mirrors the legacy console
// billing page. Payments are matched by the reference, not auto-captured.
const BANK_TRANSFER_INFO = {
  receiver: brand.paymentReceiver,
  accountNumber: '19036996598018',
  bankName: 'TECHCOMBANK',
  swiftCode: 'VTCBVNVX',
  branch: 'HO CHI MINH',
};

function BankTab({
  invoiceId,
  amount,
  customerId,
  stamp,
}: {
  invoiceId: string;
  amount: number;
  customerId?: string | number;
  stamp: number;
}) {
  const reference = generatePaymentReference(customerId, invoiceId, stamp);
  const generate = useGenerateVietQR();
  const qr = generate.data as VietQRResult | undefined;

  // Auto-generate the VietQR on open (like the legacy console) and count the
  // payment-info funnel step. Keyed on the reference so it regenerates if the
  // invoice context changes.
  useEffect(() => {
    trackAddPaymentInfo('bank', invoiceId);
    // purpose = the transfer memo the QR encodes — use the reference so the
    // banking app prefills "INV…" (matches the manual Reference row), not a
    // generic "Invoice <id>".
    generate.mutate({ invoiceId, amount, purpose: reference, reference });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, amount, reference]);

  return (
    <div className={styles.payTab}>
      <p className={styles.payIntro}>
        Scan the QR code with any banking app, or transfer manually to the account below. Include the reference so we
        can match your payment automatically.
      </p>
      {generate.isPending && (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--vn-space-6)' }}>
          <Spinner />
        </div>
      )}
      {qr?.qrCodeBase64 && !generate.isPending && (
        <div className={styles.qrWrap}>
          <img src={dataUri(qr.qrCodeBase64)} alt="QR payment code" />
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 'var(--vn-font-size-xs)',
              color: 'var(--vn-fg-subtle)',
            }}
          >
            <Lock size={13} /> Scan with your banking app to pay
          </span>
        </div>
      )}
      <div className={styles.payInfo}>
        <div className={styles.payInfoRow}>
          <span>Receiver</span>
          <span>{BANK_TRANSFER_INFO.receiver}</span>
        </div>
        <div className={styles.payInfoRow}>
          <span>Account number</span>
          <span>{BANK_TRANSFER_INFO.accountNumber}</span>
        </div>
        <div className={styles.payInfoRow}>
          <span>Bank</span>
          <span>{BANK_TRANSFER_INFO.bankName}</span>
        </div>
        <div className={styles.payInfoRow}>
          <span>SWIFT</span>
          <span>{BANK_TRANSFER_INFO.swiftCode}</span>
        </div>
        <div className={styles.payInfoRow}>
          <span>Branch</span>
          <span>{BANK_TRANSFER_INFO.branch}</span>
        </div>
        <div className={styles.payInfoRow}>
          <span>Amount</span>
          <span>{formatCurrency(amount)}</span>
        </div>
        <div className={styles.payInfoRow}>
          <span>Reference</span>
          <span>{reference}</span>
        </div>
      </div>
      <Alert tone="info">
        Use <strong>{reference}</strong> as the transfer reference so your payment is matched to this invoice.
      </Alert>
      {generate.isError && (
        <Alert tone="danger">Could not generate the QR code — you can still transfer manually using the details above.</Alert>
      )}
    </div>
  );
}

function MoMoTab({ invoiceId, amount }: { invoiceId: string; amount: number }) {
  const [orderId, setOrderId] = useState<string | null>(null);
  const create = useCreateMoMoPayment({ onSuccess: (r) => setOrderId(r.orderId) });
  const momo = create.data as MoMoResult | undefined;
  const { data: status } = useMoMoStatus(orderId);
  const paid = status?.resultCode === 0;

  // MoMo confirms via polling — count the purchase when the poll flips to paid.
  useEffect(() => {
    if (paid) trackPurchase(invoiceId, amount, 'momo');
  }, [paid, invoiceId, amount]);

  return (
    <div className={styles.payTab}>
      <p className={styles.payIntro}>
        Generate a MoMo payment, then scan the QR or open the MoMo app to pay {formatCurrency(amount)}.
      </p>
      {!momo ? (
        <Button
          disabled={create.isPending}
          onClick={() => {
            trackAddPaymentInfo('momo', invoiceId);
            create.mutate({
              orderId: `MOMO${invoiceId}-${Date.now()}`,
              amount,
              orderInfo: `Invoice ${invoiceId}`,
              invoiceId,
            });
          }}
        >
          <Wallet size={15} /> {create.isPending ? 'Generating…' : 'Generate MoMo payment'}
        </Button>
      ) : (
        <>
          {(momo.qrCodeBase64 || momo.qrCodeUrl) && (
            <div className={styles.qrWrap}>
              <img
                src={momo.qrCodeBase64 ? dataUri(momo.qrCodeBase64) : momo.qrCodeUrl}
                alt="MoMo payment code"
              />
            </div>
          )}
          {momo.payUrl && (
            <Button variant="secondary" onClick={() => window.open(momo.payUrl, '_blank', 'noopener')}>
              <ExternalLink size={15} /> Open in MoMo
            </Button>
          )}
          {paid ? (
            <Alert tone="success" title="Payment received">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={15} /> This invoice has been paid.
              </span>
            </Alert>
          ) : (
            <p className={styles.payIntro}>Waiting for payment confirmation…</p>
          )}
        </>
      )}
      {create.isError && <Alert tone="danger">Could not create the MoMo payment.</Alert>}
    </div>
  );
}

function PayPalTab({
  invoiceId,
  amount,
  customerId,
  stamp,
}: {
  invoiceId: string;
  amount: number;
  customerId?: string | number;
  stamp: number;
}) {
  const create = useCreatePayPalOrder();
  const order = create.data;
  const usd = convertVndToUsd(amount);

  return (
    <div className={styles.payTab}>
      <p className={styles.payIntro}>
        Pay with PayPal (≈ ${usd} USD). You'll be redirected to PayPal to approve the payment.
      </p>
      {!order ? (
        <Button
          disabled={create.isPending}
          onClick={() => {
            trackAddPaymentInfo('paypal', invoiceId);
            create.mutate({
              amount,
              currency: 'VND',
              reference: generatePaymentReference(customerId, invoiceId, stamp),
              description: `Invoice ${invoiceId}`,
              invoiceId,
              // PayPal redirects back here after approval/cancel to capture the order.
              returnUrl: `${window.location.origin}/payment/paypal/callback`,
              cancelUrl: `${window.location.origin}/payment/paypal/callback`,
            });
          }}
        >
          {create.isPending ? 'Creating order…' : 'Continue with PayPal'}
        </Button>
      ) : (
        <>
          <div className={styles.payInfo}>
            <div className={styles.payInfoRow}>
              <span>Order</span>
              <span>{order.orderId}</span>
            </div>
            <div className={styles.payInfoRow}>
              <span>Amount</span>
              <span>
                ${order.amount} {order.currency}
              </span>
            </div>
          </div>
          {order.approveUrl && (
            <Button onClick={() => window.open(order.approveUrl, '_blank', 'noopener')}>
              <ExternalLink size={15} /> Approve on PayPal
            </Button>
          )}
        </>
      )}
      {create.isError && <Alert tone="danger">Could not create the PayPal order.</Alert>}
    </div>
  );
}

function CardTab({ invoiceId, amount }: { invoiceId: string; amount: number }) {
  const { show } = useToast();
  const { data: methods, isLoading } = usePaymentMethods();
  const [adding, setAdding] = useState(false);
  const [paid, setPaid] = useState(false);

  const charge = useChargeSavedMethod({
    onSuccess: () => {
      setPaid(true);
      trackPurchase(invoiceId, amount, 'card');
      show({ tone: 'success', title: 'Payment submitted' });
    },
    onError: () => show({ tone: 'danger', title: 'Payment failed' }),
  });

  const list = methods ?? [];
  const usd = convertVndToUsd(amount);

  if (paid) {
    return (
      <div className={styles.payTab}>
        <Alert tone="success" title="Payment submitted">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle2 size={15} /> Your card payment for invoice #{invoiceId} is being processed.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className={styles.payTab}>
      {isLoading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--vn-space-6)' }}>
          <Spinner />
        </div>
      ) : adding || list.length === 0 ? (
        <>
          <p className={styles.payIntro}>Add a card to pay ≈ ${usd} USD.</p>
          <BraintreeCardForm
            makeDefault={list.length === 0}
            onSaved={() => {
              trackAddPaymentMethod('card');
              show({ tone: 'success', title: 'Card saved' });
              setAdding(false);
            }}
            onCancel={list.length > 0 ? () => setAdding(false) : undefined}
          />
        </>
      ) : (
        <>
          <p className={styles.payIntro}>Pay ≈ ${usd} USD with a saved card.</p>
          <div className={styles.methods}>
            {list.map((m) => (
              <div key={m.id} className={styles.method}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                  <CreditCard size={16} color="var(--vn-fg-muted)" />
                  {m.brand || 'Card'} {m.last4 ? `•••• ${m.last4}` : ''}
                </span>
                <Button
                  size="sm"
                  disabled={charge.isPending}
                  onClick={() => {
                    trackAddPaymentInfo('card', invoiceId);
                    charge.mutate({
                      methodId: m.id,
                      invoiceId,
                      amountVnd: amount,
                      amountUsd: usd,
                      description: `Invoice ${invoiceId}`,
                    });
                  }}
                >
                  {charge.isPending ? 'Paying…' : `Pay $${usd}`}
                </Button>
              </div>
            ))}
          </div>
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            <Plus size={14} /> Add another card
          </Button>
        </>
      )}
    </div>
  );
}
